import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { MAIN_BG_COLOR } from '../theme';
import { analytics, AnalyticsEvent } from '../services/analytics';

const PAGE = {
    fontHeading: '"DM Serif Text", serif',
    fontBody: '"Inter", sans-serif',
    sectionPaddingX: '1.25rem',
} as const;

export type MarketingFooterCtaProps = {
    title: string;
    subtitle?: string;
    ctaLabel: string;
    ctaTo: string;
    analyticsLocation: string;
};

/**
 * Shared dark footer CTA band for landing + product pages.
 * Copy is owned by each caller / productPages config — do not paraphrase here.
 */
const MarketingFooterCta: React.FC<MarketingFooterCtaProps> = ({
    title,
    subtitle,
    ctaLabel,
    ctaTo,
    analyticsLocation,
}) => {
    return (
        <Box
            sx={{
                width: '100%',
                background: MAIN_BG_COLOR,
                borderTop: '0.5px solid rgba(255,255,255,0.12)',
                py: { xs: 8, md: 10 },
                px: { xs: PAGE.sectionPaddingX, md: 4 },
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    <Typography
                        component="h2"
                        sx={{
                            fontFamily: PAGE.fontHeading,
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', md: '2.25rem' },
                            lineHeight: 1.25,
                            color: '#fff',
                            mb: subtitle ? 2 : 4,
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography
                            sx={{
                                fontFamily: PAGE.fontBody,
                                fontWeight: 400,
                                fontSize: '1rem',
                                lineHeight: 1.5,
                                color: 'rgba(255,255,255,0.72)',
                                mb: 4,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    ) : null}
                    <Button
                        component={Link}
                        to={ctaTo}
                        onClick={() => {
                            analytics.track(AnalyticsEvent.CLICK_REQUEST_DEMO, {
                                location: analyticsLocation,
                                ctaLabel,
                            });
                        }}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            background: '#fff',
                            color: MAIN_BG_COLOR,
                            borderRadius: '100px',
                            px: 2.5,
                            py: 1,
                            fontFamily: PAGE.fontBody,
                            fontWeight: 500,
                            textTransform: 'none',
                            '&:hover': { background: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        {ctaLabel}
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default MarketingFooterCta;
